import React, { useEffect, useState, useRef } from "react";
import { observer } from "mobx-react";
import layerStore from "@giscon/utils.layer-store";
import APIHelper from "@giscon/utils.api-helper-esri";

import BuisyIndator from "SandboxViewerComponent/src/libs/BuisyIndicator";

import Modal from "@mui/material/Modal";
import Paper from "@mui/material/Paper";
import GraphicSelector from "./GraphicSelector/GraphicSelector";

import { getURLParams } from "../utils/utils";

function usePrevious(value) {
  // https://reactjs.org/docs/hooks-faq.html
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}
function MapComponent({
  // urlParams,
  map,
  view,
  nls,
  // role,
  graphicSelectorOptions,
  sendData,
}) {
  const [urlParams] = useState(getURLParams());
  const [isLoading, setLoading] = useState(false);
  const [isSelectionLoading, setSelectionLoading] = useState(false);
  const [effectiveGraphicsSelectorOptions, setGraphicSelectorOptions] =
    useState(null);
  const { role, pendingRole } = layerStore;

  const setRole = (newRole) => {
    setLoading(true);
    layerStore.role = newRole;
    const { search, showLayers, hiddenOptionalLayers, doNotZoom } = urlParams;
    layerStore.layerManager
      .applyRole(newRole)
      .then((aborted) => {
        if (!aborted && newRole) {
          if (Array.isArray(showLayers)) {
            layerStore.hideOptionalLayersExcept(showLayers);
          } else if (Array.isArray(hiddenOptionalLayers)) {
            layerStore.showOptionalLayersExcept(hiddenOptionalLayers);
          }

          return new Promise((resolve, reject) => {
            if (newRole.extent) {
              const { xmin, ymin, xmax, ymax, spatialReference } =
                newRole.extent;

              const extent = APIHelper.createExtent(
                xmin,
                ymin,
                xmax,
                ymax,
                spatialReference
              );

              APIHelper.projectGeometries(
                {
                  geometries: [extent],
                  callback: (success, projectedGeometries) => {
                    if (success) {
                      // eslint-disable-next-line prefer-destructuring
                      map.homeExtent = projectedGeometries[0];
                      APIHelper.setExtent(
                        projectedGeometries[0],
                        map,
                        layerStore.layerManager.view
                      )
                        .then(() => {
                          resolve(false);
                        })
                        .catch(() => {
                          reject();
                        });
                    } else {
                      console.error("projection error");
                    }
                  },
                },
                map
              );
            } else {
              map.homeExtent = map._initialExtent;
              resolve(false);
            }
          });
        }
        return new Promise((resolve) => {
          resolve(aborted || !newRole);
        });
      })
      .then((aborted) => {
        return new Promise((resolve) => {
          if (search && !aborted) {
            layerStore.selectionManager.setSelectionAttributive(
              { layer: search.layer, name: search.attribute },
              search.value,
              () => {
                resolve(false);
              },
              !doNotZoom
            );
          } else if (!aborted) {
            resolve(false);
          } else {
            resolve(true);
          }
        });
      })
      .then((aborted) => {
        // ready
        if (!aborted) setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        console.error(e);
        alert(
          "Beim Laden der Rolle ist ein Fehler aufgetreten, bitte loggen sie sich erneut ein. Sollte das den Fehler nicht beheben starten sie bitte die Anwendung nach einem Logout erneut."
        );
      });

    if (!newRole) {
      setLoading(false);
    }
    // console.error('MapComponent - RoleUpdate - ', role);
  };
  const drawGraphicsSelector = () => {
    return (
      <Modal
        BackdropProps={{ style: { opacity: "0.4" } }}
        style={{ right: "50px" }}
        disableBackdropClick={!effectiveGraphicsSelectorOptions.allowAbort} // no close by Backdrop click
        disableEscapeKeyDown={!effectiveGraphicsSelectorOptions.allowAbort} // no close
        open={!!effectiveGraphicsSelectorOptions}
        onClose={() => {
          if (effectiveGraphicsSelectorOptions != null) {
            setGraphicSelectorOptions(null); // hide GraphicsSelector
            (
              effectiveGraphicsSelectorOptions.callback ||
              ((pselection) => {
                // eslint-disable-next-line no-console
                // console.log("Selected by GraphicSelector", pselection);
              })
            )(null);
          }
        }}
      >
        <Paper
          variant="outlined"
          style={{
            width: 350,
            bottom: 5,
            padding: 4,
            right: 5,
            boxSizing: "content-box",
            position: "fixed",
            top: 45,
            background: "#333",
            overflow: "auto",
          }}
        >
          <GraphicSelector
            map={map}
            view={view}
            nls={nls}
            title={effectiveGraphicsSelectorOptions.title || nls.chooseGrafic}
            forSelection={effectiveGraphicsSelectorOptions.forSelection}
            allowAbort={effectiveGraphicsSelectorOptions.allowAbort}
            forDeselection={effectiveGraphicsSelectorOptions.forDeselection}
            selectionCallback={(selection) => {
              // TODO: maybe to much logic at this code position, jap
              setGraphicSelectorOptions(null); // hide GraphicsSelector
              // call effectiveGraphicsSelectorOptions.callback or default callback with args as arguments
              (
                effectiveGraphicsSelectorOptions.callback ||
                ((pselection) => {
                  // eslint-disable-next-line no-console
                  // console.log("Selected by GraphicSelector", pselection);
                })
              )(selection);
            }}
            graphics={effectiveGraphicsSelectorOptions.graphics}
          />
        </Paper>
      </Modal>
    );
  };

  useEffect(() => {
    // listener functions
    const graphicsSelectionOptionsListener = (e) => {
      setGraphicSelectorOptions(
        e.data && Object.keys(e.data).length === 0 ? null : e.data
      );
    };

    const selectionEnableListener = (e) => {
      sendData({
        action: "selectionModeChanged",
        active: !!e.data,
      });
    };

    const selectionStartListener = ({ data }) => {
      if (data.error) {
        setSelectionLoading(false);
      } else {
        setSelectionLoading(true);
      }
    };

    const selectionEndListener = ({ data }) => {
      const { selectedFeatures /* , feature */ } = data;
      const selection = selectedFeatures.map((feature) => {
        const layer = layerStore.layerManager.getOriginLayer(feature);
        return { graphic: feature, layer, id: feature.internalEditorID };
      });
      sendData({
        action: "selectionChanged",
        data: selection,
      });
      layerStore.selection = selection;
      setSelectionLoading(false);
    };
    // setup eventlisteners
    layerStore.selectionManager.addEventListener(
      "trigger-show-selection-options",
      graphicsSelectionOptionsListener
    );
    layerStore.selectionManager.addEventListener(
      "selection-enable-change",
      selectionEnableListener
    );
    layerStore.selectionManager.addEventListener(
      "selection-start",
      selectionStartListener
    );
    layerStore.selectionManager.addEventListener(
      "selection-end",
      selectionEndListener
    );

    return () => {
      // remove eventListeners
      layerStore.selectionManager.removeEventListener(
        "trigger-show-selection-options",
        graphicsSelectionOptionsListener
      );
      layerStore.selectionManager.removeEventListener(
        "selection-enable-change",
        selectionEnableListener
      );
      layerStore.selectionManager.removeEventListener(
        "selection-start",
        selectionStartListener
      );
      layerStore.selectionManager.removeEventListener(
        "selection-end",
        selectionEndListener
      );
    };
  }, []);
  useEffect(() => {
    let abort = false;
    const newRole = pendingRole;
    if (
      (!role && newRole) ||
      (role && !newRole) ||
      (role &&
        newRole &&
        role.id !== undefined &&
        newRole.id !== undefined &&
        role.id !== newRole.id)
    ) {
      sendData({
        // bugfix: otherwise the selection is empty, but all widgets listening on the event listener see the old one
        action: "selectionChanged",
        data: [],
      });
      layerStore.selection = [];
      setRole(newRole);
    }
    return () => {
      abort = true;
    };
  }, [role, pendingRole]);
  useEffect(() => {
    // maybe problem when changed to null, but selection has a graphicsselector?
    setGraphicSelectorOptions(graphicSelectorOptions);
  }, [graphicSelectorOptions]);
  return (
    <>
      {effectiveGraphicsSelectorOptions && drawGraphicsSelector()}
      <BuisyIndator open={isLoading} text={nls.loadingIndicatorText} />
      <BuisyIndator
        open={!isLoading && isSelectionLoading}
        text={nls.selectionIndicatorText}
      />
    </>
  );
}
export default observer(MapComponent);
